import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  Theme,
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
} from '@material-ui/icons';
import AccountMenu from '../AccountMenu/AccountMenu';
import JoinRoomButton from '../JoinRoomButton/JoinRoomButton';
import { Link } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, addDoc, serverTimestamp, getDocs, doc, deleteDoc, orderBy, query } from 'firebase/firestore';
import { auth, db } from '../../services/firebase.config';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: 'white',
    padding: theme.spacing(3, 0),
    marginBottom: theme.spacing(4),
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
    },
  },
  title: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '2.5rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  subtitle: {
    color: '#6c757d',
    fontSize: '1.1rem',
    marginTop: theme.spacing(0.5),
  },
  createButton: {
    backgroundColor: '#2980b9',
    color: 'white',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    boxShadow: '0 3px 12px rgba(41, 128, 185, 0.3)',
    '&:hover': {
      backgroundColor: '#3498db',
      transform: 'translateY(-1px)',
      boxShadow: '0 5px 16px rgba(41, 128, 185, 0.4)',
    },
    transition: 'all 0.2s ease',
  },
  mainContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
  },
  jobCard: {
    height: '100%',
    borderRadius: theme.spacing(1.5),
    border: '1px solid #e9ecef',
    transition: 'all 0.3s ease',
    backgroundColor: 'white',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 25px rgba(44, 62, 80, 0.15)',
      borderColor: '#3498db',
    },
  },
  jobCardContent: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  jobTitle: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '1.3rem',
    marginBottom: theme.spacing(2),
    lineHeight: 1.3,
  },
  jobDetail: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    color: '#6c757d',
    fontSize: '0.95rem',
  },
  jobDetailIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1.1rem',
    color: '#95a5a6',
  },
  jobActions: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #f1f2f6',
  },
  deleteButton: {
    color: '#e74c3c',
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgba(231, 76, 60, 0.1)',
    },
  },
  viewDetailsButton: {
    color: '#2980b9',
    borderColor: '#2980b9',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'rgba(41, 128, 185, 0.05)',
      borderColor: '#3498db',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8, 2),
    color: '#6c757d',
    backgroundColor: 'white',
    borderRadius: theme.spacing(2),
    border: '1px solid #e9ecef',
  },
  emptyIcon: {
    fontSize: '5rem',
    color: '#bdc3c7',
    marginBottom: theme.spacing(3),
  },
  dialogPaper: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1),
  },
  dialogTitle: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  textField: {
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#3498db',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2980b9',
        borderWidth: 2,
      },
    },
    '& .MuiInputLabel-root': {
      '&.Mui-focused': {
        color: '#2980b9',
      },
    },
  },
  submitButton: {
    backgroundColor: '#2980b9',
    color: 'white',
    textTransform: 'none',
    padding: theme.spacing(1, 3),
    '&:hover': {
      backgroundColor: '#3498db',
    },
  },
  statsGrid: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    padding: theme.spacing(3),
    backgroundColor: 'white',
    borderRadius: theme.spacing(1.5),
    border: '1px solid #e9ecef',
    textAlign: 'center',
    height: '100%',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
    },
  },
  statIcon: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
  },
  totalIcon: {
    color: '#3498db',
  },
  completedIcon: {
    color: '#27ae60',
  },
  activeIcon: {
    color: '#f39c12',
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    color: '#6c757d',
    fontSize: '1rem',
    fontWeight: 500,
  },
  jobsGrid: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '1.5rem',
    marginBottom: theme.spacing(3),
  },
}));

const JobList = () => {
  const [jobs, setJob] = useState<{ id: string; [key: string]: any }[]>([]);
  const collectionRef = collection(db, 'job');
  const [createJob, setCreateJob] = useState('');
  const [jobAddress, setJobAddress] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const classes = useStyles();

  useEffect(() => {
    const authStateListener = onAuthStateChanged(auth, currentUser => {
      if (currentUser) {
        setUser(currentUser);
        console.log('uid', currentUser.uid);
      } else {
        console.log('user is logged out');
      }
    });

    return () => {
      authStateListener();
    };
  }, []);

  const submitJob = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) {
      alert('Please login before creating a job');
      return;
    }

    if (!createJob.trim() || !jobAddress.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await addDoc(collectionRef, {
        job: createJob,
        address: jobAddress,
        userId: user?.uid,
        isChecked: false,
        timestamp: serverTimestamp(),
      });
      setCreateJob('');
      setJobAddress('');
      setDialogOpen(false);
      // Refresh the jobs list
      getJob();
    } catch (err) {
      console.log(err);
    }
  };

  const getJob = useCallback(async () => {
    const q = query(collectionRef, orderBy('timestamp', 'desc'));
    try {
      const jobSnapshot = await getDocs(q);
      const jobData = jobSnapshot.docs.map(jobDoc => ({ ...jobDoc.data(), id: jobDoc.id }));
      setJob(jobData);
    } catch (err) {
      console.log(err);
    }
  }, [collectionRef]);

  useEffect(() => {
    getJob();
  }, [getJob]);

  const deleteJob = async (id: string) => {
    try {
      const userConfirmed = window.confirm('Are you sure you want to delete this job?');
      if (userConfirmed) {
        const documentRef = doc(db, 'job', id);
        await deleteDoc(documentRef);
        getJob(); // Refresh the list
      }
    } catch (err) {
      console.log(err);
    }
  };

  const totalJobs = jobs.length;
  const completedJobs = jobs.filter(job => job.isChecked).length;
  const activeJobs = jobs.filter(job => !job.isChecked).length;

  return (
    <div className={classes.root}>
      <AccountMenu />

      <Box className={classes.header}>
        <Container maxWidth={false} className={classes.mainContent}>
          <Box className={classes.headerContent}>
            <Box>
              <Typography variant="h3" className={classes.title}>
                Job Dashboard
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                Manage your HVAC service jobs and connect with technicians
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              className={classes.createButton}
              onClick={() => setDialogOpen(true)}
            >
              New Job
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth={false} className={classes.mainContent}>
        {/* Enhanced Stats Section */}
        <Grid container spacing={3} className={classes.statsGrid}>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statCard}>
              <AssessmentIcon className={`${classes.statIcon} ${classes.totalIcon}`} />
              <Typography className={classes.statNumber} style={{ color: '#3498db' }}>
                {totalJobs}
              </Typography>
              <Typography className={classes.statLabel}>Total Jobs</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statCard}>
              <CheckCircleIcon className={`${classes.statIcon} ${classes.completedIcon}`} />
              <Typography className={classes.statNumber} style={{ color: '#27ae60' }}>
                {completedJobs}
              </Typography>
              <Typography className={classes.statLabel}>Completed</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className={classes.statCard}>
              <ScheduleIcon className={`${classes.statIcon} ${classes.activeIcon}`} />
              <Typography className={classes.statNumber} style={{ color: '#f39c12' }}>
                {activeJobs}
              </Typography>
              <Typography className={classes.statLabel}>Active</Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Jobs Section */}
        {jobs.length === 0 ? (
          <Box className={classes.emptyState}>
            <WorkIcon className={classes.emptyIcon} />
            <Typography variant="h4" style={{ marginBottom: 16, fontWeight: 'bold' }}>
              No jobs yet
            </Typography>
            <Typography variant="body1" style={{ marginBottom: 24, fontSize: '1.1rem' }}>
              Create your first job to get started with HVAC technician collaboration
            </Typography>
            <Button
              variant="contained"
              className={classes.createButton}
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Create First Job
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="h4" className={classes.sectionTitle}>
              Recent Jobs ({jobs.length})
            </Typography>
            <Grid container spacing={3} className={classes.jobsGrid}>
              {jobs.map(({ job, address, id, timestamp }) => (
                <Grid item xs={12} sm={6} lg={4} key={id}>
                  <Card className={classes.jobCard}>
                    <CardContent className={classes.jobCardContent}>
                      <Typography className={classes.jobTitle}>{job}</Typography>

                      <Box className={classes.jobDetail}>
                        <LocationIcon className={classes.jobDetailIcon} />
                        {address}
                      </Box>

                      <Box className={classes.jobDetail}>
                        <TimeIcon className={classes.jobDetailIcon} />
                        {timestamp ? new Date(timestamp.seconds * 1000).toLocaleString() : 'Just now'}
                      </Box>

                      <Box className={classes.jobActions}>
                        <Box display="flex" style={{ gap: '8px' }}>
                          <Link to={`/job/${id}`} style={{ textDecoration: 'none' }}>
                            <Button variant="outlined" size="small" className={classes.viewDetailsButton}>
                              View Details
                            </Button>
                          </Link>
                          <JoinRoomButton
                            jobId={id}
                            roomName={job}
                            displayName={user?.displayName || 'Technician'}
                            showIcon={false}
                          />
                        </Box>
                        <IconButton className={classes.deleteButton} onClick={() => deleteJob(id)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>

      {/* Create Job Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={submitJob}>
          <DialogTitle className={classes.dialogTitle}>Create New Job</DialogTitle>
          <DialogContent>
            <TextField
              label="Job Name"
              placeholder="e.g., HVAC Repair - Main Office"
              value={createJob}
              onChange={e => setCreateJob(e.target.value)}
              fullWidth
              required
              className={classes.textField}
              variant="outlined"
            />
            <TextField
              label="Job Address"
              placeholder="e.g., 123 Main St, Boston, MA 02101"
              value={jobAddress}
              onChange={e => setJobAddress(e.target.value)}
              fullWidth
              required
              className={classes.textField}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions style={{ padding: '16px 24px' }}>
            <Button onClick={() => setDialogOpen(false)} style={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button type="submit" className={classes.submitButton}>
              Create Job
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default JobList;
